import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getCountryForUi } from "../../store/reducer/country/countrySlice";
import { getCustomerById } from "../../store/reducer/customers/customersSlice";
import {
  getCustomerSiteById,
  updateCustomerSite,
} from "../../store/reducer/customers/customerSiteSlice";
import { getCustomerContactForUI } from "../../store/reducer/customers/customerContactSlice";

function EditSite() {
  const { id, site_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { options = [] } = useSelector((state) => state.ddOptions);
  const { countries = [] } = useSelector((state) => state.countries);

  const [customerData, setCustomerData] = useState([]);
  const [customerSiteData, setCustomerSiteData] = useState([]);
  const [customerContactData, setcustomerContactData] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ───────────────────────────────────────────
     FETCH STATIC DATA
  ─────────────────────────────────────────── */
  useEffect(() => {
    dispatch(getCustomerById(id)).then((r) => r.payload && setCustomerData(r.payload));
    dispatch(getCustomerSiteById(site_id)).then((r) => {
      if (r.payload) {
        setCustomerSiteData(r.payload);
        setLoading(false);
      }
    });
    dispatch(getCustomerContactForUI(id)).then((r) => r.payload && setcustomerContactData(r.payload));
    dispatch(getDdOptionForUI());
    dispatch(getCountryForUi());
  }, [dispatch, id, site_id]);

  /* ───────────────────────────────────────────
     VALIDATION
  ─────────────────────────────────────────── */
  const validationSchema = Yup.object({
    /* column 1 */
    site_name: Yup.string().required("required"),
    street: Yup.string().required("required"),
    city: Yup.string().required("required"),
    country: Yup.number().required("required"),
    post_code: Yup.string().required("required"),
    customer_contact_id: Yup.number().required("required"),

    /* column 2 */
    billing_name: Yup.string().required("required"),
    billing_street: Yup.string().required("required"),
    billing_city: Yup.string().required("required"),
    billing_post_code: Yup.string().required("required"),
    billing_country: Yup.number().required("required"),
  });

  /* ───────────────────────────────────────────
     INITIAL VALUES
  ─────────────────────────────────────────── */
  const initialValues = {
    /* column 1 */
    site_name: customerSiteData?.site_name || "",
    street: customerSiteData?.street || "",
    city: customerSiteData?.city || "",
    country: customerSiteData?.country || "",
    post_code: customerSiteData?.post_code || "",
    customer_contact_id: customerSiteData?.customer_contact_id || "",

    /* column 2 */
    billing_name: customerSiteData?.billing_name || "",
    billing_street: customerSiteData?.billing_street || "",
    billing_city: customerSiteData?.billing_city || "",
    billing_post_code: customerSiteData?.billing_post_code || "",
    billing_country: customerSiteData?.billing_country || "",
    vat: customerSiteData?.vat || "",
    eori_number: customerSiteData?.eori_number || "",

    /* other */
    state: customerSiteData?.state || "",
    status: customerSiteData?.status || "Active",
    copyCustomerDetails: false,
    copyCustomerToBilling: false,
    copySiteToBilling: false,
  };

  /* ───────────────────────────────────────────
     SUBMIT
  ─────────────────────────────────────────── */
  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    values.updatedBy = JSON.parse(localStorage.getItem("userSession")).id;

    const formData = new FormData();
    Object.entries(values).forEach(([k, v]) => formData.append(k, v));

    await dispatch(updateCustomerSite({ data: formData, id: site_id })).then((res) => {
      if (res.payload) {
        toast.success(res.payload.message);
        navigate(`/customer/${id}/site`);
      } else {
        toast.error(res.error.message);
      }
    });
  };

  /* ───────────────────────────────────────────
     RENDER
  ─────────────────────────────────────────── */
  if (loading)
    return (
      <div className="text-center">
        <FontAwesomeIcon icon={faSpinner} size="2xl" style={{ color: "#55A14A" }} spin />
      </div>
    );

  return (
    <>
      {/* -------- Header -------- */}
      <div className="flex justify-between mb-[30px]">
        <div className="flex gap-[16px] items-center">
          <Link to={`/customer/${id}/site`}>
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] text-[14px] text-[#9E9E9E] leading-[16px]">
              Site / Edit
            </span>
            <span className="font-Inter font-[700] text-[24px] leading-[30px]">
              Edit Site
            </span>
          </div>
        </div>
      </div>

      {/* -------- Form -------- */}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, setFieldValue, values }) => (
          <Form>
            {/* Two-column wrapper */}
            <div className="grid md:grid-cols-2 gap-[24px]">
              {/* Copy Customer Details Checkbox */}
              {/* ─────────── Column 1 ─────────── */}
              <div className="bg-[#F8F8F8] rounded-[12px] p-6 flex flex-col gap-4">
                <div className="mb-4">
                  <label className="inline-flex items-center gap-2 p-2">
                    <Field
                      type="checkbox"
                      name="copyCustomerDetails"
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setFieldValue("site_name", customerData?.customer_name || "");
                          setFieldValue("street", customerData?.street || "");
                          setFieldValue("city", customerData?.city || "");
                          setFieldValue("country", customerData?.country || "");
                          setFieldValue("post_code", customerData?.post_code || "");
                        }
                      }}
                    />
                    <span className="font-Inter text-[15px] text-[#1C1C20]">Copy Customer Details</span>
                  </label>
                </div>
                <FormField
                  label="Site Name"
                  name="site_name"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <FormField
                  label="Site Street"
                  name="street"
                  component="textarea"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <FormField
                  label="Site City"
                  name="city"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <SelectField
                  label="Site Country"
                  name="country"
                  options={countries}
                  optionLabel="short_name"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <FormField
                  label="Site Post Code"
                  name="post_code"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <SelectField
                  label="Site Contact"
                  name="customer_contact_id"
                  options={customerContactData}
                  optionLabel={(c) => `${c.firstname} ${c.lastname}`}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
              </div>

              {/* ─────────── Column 2 ─────────── */}
              <div className="bg-[#F8F8F8] rounded-[12px] p-6 flex flex-col gap-4">
                <div className="mb-4">
                  <label className="inline-flex items-center gap-2 p-2">
                    <Field
                      type="checkbox"
                      name="copyCustomerToBilling"
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setFieldValue("billing_name", customerData?.customer_name || "");
                          setFieldValue("billing_street", customerData?.street || "");
                          setFieldValue("billing_city", customerData?.city || "");
                          setFieldValue("billing_country", customerData?.country || "");
                          setFieldValue("billing_post_code", customerData?.post_code || "");
                        }
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Copy Customer Details to Billing
                    </span>
                  </label>

                  <label className="inline-flex items-center gap-2 p-2">
                    <Field
                      type="checkbox"
                      name="copySiteToBilling"
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setFieldValue("billing_name", values.site_name || "");
                          setFieldValue("billing_street", values.street || "");
                          setFieldValue("billing_city", values.city || "");
                          setFieldValue("billing_country", values.country || "");
                          setFieldValue("billing_post_code", values.post_code || "");
                        }
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Copy Site Details to Billing
                    </span>
                  </label>
                </div>

                <FormField
                  label="Billing Name"
                  name="billing_name"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <FormField
                  label="Billing Street"
                  name="billing_street"
                  component="textarea"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <FormField
                  label="Billing City"
                  name="billing_city"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <SelectField
                  label="Billing Country"
                  name="billing_country"
                  options={countries}
                  optionLabel="short_name"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <FormField
                  label="Billing Post Code"
                  name="billing_post_code"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <SelectField
                  label="Vat"
                  name="vat"
                  options={options.filter(
                    (o) => o.ui_name === "Finance" && o.field_name === "Tax_Rate"
                  )}
                  optionLabel={(o) => `${o.field_code}`}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
                <FormField
                  label="EORI Number"
                  name="eori_number"
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
              </div>
            </div>

            {/* -------- Status + Submit -------- */}
            <div className="flex gap-[24px] bg-[#F8F8F8] rounded-[12px] mt-5 p-4">
              {/* Status toggle */}
              <div className="flex-1">
                <label className="font-Inter font-[400] text-[15px] text-[#9E9E9E]">
                  Status
                </label>
                <div>
                  <label className="inline-flex items-center px-5 py-2 cursor-pointer text-gray-800 bg-white rounded-[12px]">
                    <Field
                      type="checkbox"
                      name="status"
                      className="hidden peer"
                      onChange={(e) =>
                        setFieldValue("status", e.target.checked ? "Active" : "Inactive")
                      }
                      checked={initialValues.status === "Active"}
                    />
                    <span className="px-4 py-1 rounded-lg border-gray-600 border peer-checked:border-0">
                      Inactive
                    </span>
                    <span className="px-4 py-1 rounded-lg border-gray-600 peer-checked:border">
                      Active
                    </span>
                  </label>
                </div>
              </div>

              {/* Submit */}
              <div>
                <button
                  type="submit"
                  className={`py-[12px] px-[48px] text-white rounded-[12px] text-[18px] ${
                    isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

/* ───────────────────────────────────────────
   SMALL REUSABLE FIELD WRAPPERS
────────────────────────────────────────── */
const FormField = ({
  label,
  name,
  component = "input",
  setFieldValue,
  errors,
  touched,
}) => (
  <div>
    <label className="font-Inter text-[15px] text-[#9E9E9E]" htmlFor={name}>
      {label}
    </label>
    <Field
      as={component}
      id={name}
      name={name}
      className="block w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
      onChange={(e) => setFieldValue(name, e.target.value)}
    />
    {errors[name] && touched[name] && <div className="text-red-700">{errors[name]}</div>}
  </div>
);

const SelectField = ({
  label,
  name,
  options,
  optionLabel,
  optionValue = "id",
  setFieldValue,
  errors,
  touched,
}) => (
  <div>
    <label className="font-Inter text-[15px] text-[#9E9E9E]" htmlFor={name}>
      {label}
    </label>
    <div className="relative">
      <Field
        as="select"
        id={name}
        name={name}
        className="block w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
        onChange={(e) => setFieldValue(name, e.target.value)}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt[optionValue]}>
            {typeof optionLabel === "function" ? optionLabel(opt) : opt[optionLabel]}
          </option>
        ))}
      </Field>
    </div>
    {errors[name] && touched[name] && <div className="text-red-700">{errors[name]}</div>}
  </div>
);

export default EditSite;
